import React, { useRef, Suspense } from 'react';
import styled from 'styled-components';
import {
  Typography,
  Box,
  Container,
  Section,
  DetailsLink,
  Figure,
  Image,
  Layout,
  Grid,
  Link,
  Button,
} from '../components';
import expertiseImg from '../images/expertise/header.svg';
import { featuresExpertise } from '../config/data';
import { useOnScreen, useVisibleContactForm } from '../hooks';

const ContactForm = React.lazy(() => import('../components/ContactForm'));

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-direction: column;

  @media (min-width: 600px) {
    flex-direction: row;
  }
`;

const GridContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const serviceItems = [
  { label: 'Web Application Development', link: '/application-development/' },
  { label: 'Mobile App Development', link: '/mobile-app-development/' },
  { label: 'Managed IT Services', link: '/managed-it-services/' },
  { label: 'AI, Data & Intelligent Automation', link: '/ai-ml-solution/' },
  { label: 'E-commerce & Shopify', link: '' },
  { label: 'UI/UX Design', link: '' },
  { label: 'Backend Engineering', link: '' },
  { label: 'API Integration', link: '' },
  { label: 'Database Engineering', link: '' },
  { label: 'QA & Testing', link: '' },
  { label: 'DevOps & Cloud Infrastructure', link: '/cloud-service/' },
  { label: 'Application Modernization', link: '' },
];

const TechRow = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  align-items: baseline;
  padding: 20px 0;
  border-bottom: 1px solid #152539;

  &:first-child {
    border-top: 1px solid #152539;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TechRowLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  color: #f66d1d;
  padding-right: 24px;
`;

const TechRowItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 0;
`;

const TechRowItem = styled.span`
  font-size: 14px;
  color: #94b8cc;

  &:not(:last-child)::after {
    content: '·';
    margin: 0 10px;
    color: #2a4a6b;
  }
`;

const techGroups = [
  {
    title: 'Frontend Engineering',
    items: [
      'React',
      'Next.js',
      'Angular',
      'SvelteKit',
      'Tailwind CSS',
      'ShadCN UI',
      'Radix UI',
      'WebSocket',
    ],
  },
  {
    title: 'Mobile App Development',
    items: [
      'React Native',
      'Expo',
      'Native mobile integrations',
      'App Store / Play Store deployment',
    ],
  },
  {
    title: 'Backend Engineering',
    items: ['Node.js', 'TypeScript', 'Express.js', '.NET Core', 'Python'],
  },
  {
    title: 'Database & Data Layer',
    items: [
      'PostgreSQL',
      'MySQL',
      'MongoDB',
      'SqlServer',
      'Prisma',
      'Cosmos DB',
    ],
  },
  {
    title: 'Cloud & DevOps',
    items: ['AWS', 'Azure', 'Docker', 'CI/CD', 'Terraform'],
  },
  {
    title: 'Automation & Integrations',
    items: [
      'REST APIs',
      'Third-party SDKs',
      'Payment gateways',
      'Workflow automation',
    ],
  },
  {
    title: 'AI & Data',
    items: ['OpenAI', 'Claude', 'LangChain', 'Azure AI Search', 'OpenCV'],
  },
  {
    title: 'E-commerce',
    items: ['WordPress', 'Custom storefronts', 'Payment/order automation'],
  },
];
const ExpertisePage = () => {
  const ref = useRef();
  const onScreen = useOnScreen(ref, '-50px');
  const showContactForm = useVisibleContactForm(onScreen);

  return (
    <Layout title="Expertise">
      <Container mt={{ _: 8, md: 12 }}>
        <Section>
          <Grid gridTemplateColumns={{ _: '1fr', sm: '3fr 2fr' }}>
            <Box>
              <Typography variant="heading1" mb={1}>
                Complete Enterprise Solutions
              </Typography>
              <Typography
                variant="heading4"
                color=""
                mb={2}
                mt={{ _: 1, md: 3 }}
              >
                Grow your business with our market-leading experts.
              </Typography>
              <Typography variant="paragraph1" fontWeight="normal" mb={4}>
                Our highly proficient engineers are trained to adapt to any
                business development environment and efficiently fulfill
                business demand. We offer expertise in all mainstream technology
                stacks and produce the output exactly as imagined by our client.
              </Typography>
            </Box>
            <Box display={{ _: 'none', sm: 'block' }}>
              <Figure
                src={expertiseImg}
                alt="Improve your Business now!"
                barColor="#008FD4"
                barPosition="left"
                position="right"
                width="80%"
              />
            </Box>
          </Grid>
        </Section>
        <Section>
          <Grid mt={4} gridTemplateColumns={{ _: '1fr', sm: '1fr 1fr' }}>
            {serviceItems.map(item => (
              <Box key={item.label}>
                <DetailsLink
                  to={item.link}
                  variant="underline"
                  showStartArrow
                  disabled={!item.link}
                >
                  {item.label}
                </DetailsLink>
              </Box>
            ))}
          </Grid>
        </Section>
        <Section ref={ref}>
          <Typography variant="heading3" mb={8}>
            Overwhelmed with finding expertise? See our simplified service
            models.
          </Typography>

          <Grid gridTemplateColumns={{ _: '1fr', sm: '1fr 1fr 1fr' }}>
            {featuresExpertise.map(feature => (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                textAlign="center"
                key={feature.title}
              >
                <Image mb={2} src={feature.image} alt={feature.title} />
                <Typography variant="heading5" my={1}>
                  {feature.title}
                </Typography>
                <Typography variant="body2">{feature.content}</Typography>
                <DetailsLink to={feature.link} mt={2}>
                  {feature.linkLabel}
                </DetailsLink>
              </Box>
            ))}
          </Grid>
        </Section>

        <Section>
          <Typography style={{ textAlign: 'center' }} variant="heading3" mb={6}>
            Technologies We Have Expertise In
          </Typography>
          <Box>
            {techGroups.map(group => (
              <TechRow key={group.title}>
                <TechRowLabel>{group.title}</TechRowLabel>
                <TechRowItems>
                  {group.items.map(item => (
                    <TechRowItem key={item}>{item}</TechRowItem>
                  ))}
                </TechRowItems>
              </TechRow>
            ))}
          </Box>
        </Section>
        <Section>
          <Grid gridTemplateColumns={{ _: '1fr', sm: '1fr' }}>
            <Box>
              <Typography
                style={{ textAlign: 'center' }}
                variant="heading3"
                mb={3}
              >
                Ready To Build Your Brand?
              </Typography>
            </Box>
          </Grid>
          <GridContainer>
            <ButtonContainer>
              <Link to="/contact/">
                <Button variant="contained" color="primary" primary>
                  Contact Us
                </Button>
              </Link>
              <Link to="/case-studies/">
                <Button variant="contained" color="primary">
                  View All Services
                </Button>
              </Link>
            </ButtonContainer>
          </GridContainer>
        </Section>
        {showContactForm && (
          <Section>
            <Suspense fallback={<div />}>
              <ContactForm />
            </Suspense>
          </Section>
        )}
      </Container>
    </Layout>
  );
};

export default ExpertisePage;
